import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

interface TestimonialFormProps {
  memberId: string;
}

interface ExistingTestimonial {
  id: number;
  text: string;
  status: string;
}

export default function TestimonialForm({ memberId }: TestimonialFormProps) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [existing, setExisting] = useState<ExistingTestimonial | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await supabase
          .from("testimonials")
          .select("id, text, status")
          .eq("member_id", Number(memberId))
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!cancelled && data) {
          setExisting(data);
          setText(data.text || "");
        }
      } catch {
        // query failed silently, just show empty form
      }
    }
    load();
    return () => { cancelled = true; };
  }, [memberId]);

  const statusLabel: Record<string, { label: string; color: string; icon: string }> = {
    pending: { label: "Pendente de aprovação", color: "text-yellow-600 bg-yellow-50 border-yellow-200", icon: "ri-time-line" },
    approved: { label: "Publicado", color: "text-green-600 bg-green-50 border-green-200", icon: "ri-checkbox-circle-line" },
    rejected: { label: "Não aprovado", color: "text-red-500 bg-red-50 border-red-200", icon: "ri-close-circle-line" },
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim() || text.trim().length < 10) {
      setError("Escreva pelo menos 10 caracteres no seu depoimento.");
      return;
    }
    if (text.trim().length > 1000) {
      setError("O depoimento deve ter no máximo 1000 caracteres.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess(false);

    const { error: dbError } = await supabase
      .from("testimonials")
      .upsert({
        member_id: Number(memberId),
        text: text.trim(),
        status: existing?.status === "approved" ? "approved" : "pending",
        updated_at: new Date().toISOString(),
      }, {
        onConflict: "member_id",
      });

    if (dbError) {
      setError("Erro ao salvar depoimento. Tente novamente.");
      setSaving(false);
      return;
    }

    setSuccess(true);
    setSaving(false);

    // Refresh existing state
    const { data } = await supabase
      .from("testimonials")
      .select("id, text, status")
      .eq("member_id", Number(memberId))
      .maybeSingle();

    if (data) {
      setExisting(data);
    }
  };

  return (
    <section id="testimonial-form" className="pb-8 md:pb-12">
      <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
        <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm">
          <div className="flex items-start gap-4 mb-5">
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 shrink-0 mt-0.5">
              <i className="ri-chat-quote-line text-accent-700 text-lg" />
            </span>
            <div className="flex-1">
              <h3 className="font-display text-lg text-brown mb-1">
                Deixe seu depoimento sobre a comunidade Entre Elas
              </h3>
              <p className="font-body text-sm text-brown/50 leading-relaxed">
                Compartilhe como a comunidade impactou sua vida. Seu depoimento poderá aparecer
                na página inicial para inspirar outras mulheres. Ele passará por uma revisão antes de ser publicado.
              </p>
            </div>
          </div>

          {/* Status badge for existing testimonial */}
          {existing && existing.status !== "pending" && (
            <div className={`mb-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-body ${statusLabel[existing.status]?.color || ""}`}>
              <i className={statusLabel[existing.status]?.icon || "ri-information-line"} />
              {statusLabel[existing.status]?.label || existing.status}
            </div>
          )}

          {existing?.status === "pending" && (
            <div className="mb-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-50 border border-yellow-200 text-yellow-600 font-body text-xs">
              <i className="ri-time-line" />
              Aguardando aprovação da administradora
            </div>
          )}

          {success && (
            <div className="mb-5 flex items-center gap-2 px-4 py-3 rounded-lg bg-green-50 text-green-700 font-body text-sm">
              <i className="ri-check-line text-lg" />
              Depoimento salvo com sucesso!
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <textarea
                value={text}
                onChange={(e) => { setText(e.target.value); setError(""); setSuccess(false); }}
                placeholder="Conte como a comunidade Entre Elas fez diferença na sua vida, o que você encontrou aqui, como se sente fazendo parte..."
                rows={5}
                maxLength={1000}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors resize-none"
              />
              <div className="flex items-center justify-between mt-2">
                <p className="font-body text-xs text-brown/40">
                  Mínimo 10 caracteres
                </p>
                <p className={`font-body text-xs ${text.length > 1000 ? "text-red-500" : "text-brown/40"}`}>
                  {text.length}/1000
                </p>
              </div>
            </div>

            {error && (
              <p className="font-body text-sm text-red-500">{error}</p>
            )}

            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-white font-body text-sm tracking-wide uppercase hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Salvando...
                </>
              ) : existing ? (
                <>
                  <i className="ri-edit-line" />
                  Atualizar depoimento
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line" />
                  Enviar depoimento
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}